import * as _ from "lodash";
import {ASync} from "../PA.Core/Basic/ASync";
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {
    IMessageProvider,
    IProgressProvider, MessageType
} from "../PA.Core/PA.Core.IF.Basic";
import {IBusinessObject} from "../PA.Core/PA.Core.IF.BO";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {
    CreateOptions,
    ESRecordResolver
} from "../PA.Core/lib/ESRecordResolver"
import {PAdbContext} from "../PA.Core/PAdbContext";
import {
    esWB_Customer_Type,
    esWB_DataObject,
    esWB_Entity,
    esWB_Customer_Role,
    esWB_MasterData_Set,
    esWB_UI_AppExecutable
} from "../PA.Core/PAdbSchema";
import {WB_CustomerRoleRow, WB_CustomerTypeRow} from "../PA.Core/PAdbSchemaRows";
import {IwbCustomerType} from "../PA.Core/PAdbSchemaRowsIF";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {RecordUtils} from "../PA.Core/Basic/Records";
import {CustomerTypeBOValidations} from "./CustomerTypeBO.Validations";
import {IEntityMetaData} from "../PA.MD.Core/PA.MD.IF.Entity";
import {EntityMetaDataManager} from "../PA.MD.Core/EntityMetaDataManager";

export class CustomerTypeBO implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOption: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_CustomerType: WB_CustomerTypeRow;

    constructor(options?: any) {
        this.CreateOptions = options || {};
        if ('dbContext' in this.CreateOptions) {
            this.dbContext = this.CreateOptions.dbContext;
        }

        this.Identity = new IdentityProvider({id: 0, name: 'New Customer Type'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOption = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider('Validating', this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider('Saving', this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider('Deleting', this);

        this.drWB_CustomerType = null;

        this.IEntity_init()
    }

    //</editor-fold>

    //<editor-fold desc = "IBusinessObject operations">
    get WB_CustomerType(): WB_CustomerTypeRow {
        return this.drWB_CustomerType;
    }

    async LoadNew(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Customer Type");
        let olc = self._OnLoadComplete = (async () => {
            self.LoadOptions = options || {};

            try {
                if ('dbContext' in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext;
                } else {
                    self.dbContext = new PAdbContext();
                }

                if (this.dbContext == null) {
                    const sError = 'Unable to determine dbContext for new Customer Type.';
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return this;
                }

                let newCustomerTypeRow = <WB_CustomerTypeRow>await this.dbContext.createRecordWithKey(esWB_Customer_Type)
                newCustomerTypeRow.setDefaults();
                self.drWB_CustomerType = newCustomerTypeRow;
                self.dbContext.add(esWB_Customer_Type, newCustomerTypeRow);
                this.WB_CustomerType.cif.WB_MDSET_ID = options?.wbMasterDataSetId || 0
                this.WB_CustomerType.cif.CUSTM_ID = options?.sId || this.WB_CustomerType.cif.CUSTM_ID || ''
                this.WB_CustomerType.cif.CUSTM_NAME = options?.sName || this.WB_CustomerType.cif.CUSTM_NAME || ''
                self.updateIdentity();
                await self.IEntity_OnLoadComplete()

                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;

                self._OnLoadComplete = undefined;
                return self;
            } catch (err) {
                self.LoadProgressProvider.exception(err);
                return self;
            }

        })();
        return olc;
    }

    async Load(anId: number | string, options?: object): Promise<IBusinessObject> {
        const self = this;
        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start('Loading (' + self.Identity.id_ui + ')');
        let olc = this._OnLoadComplete = (async () => {
            try {
                if (self.bIsLoaded) {
                    const sError = 'Customer Type already loaded.';
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }
                self.LoadOptions = options || {};

                if ('dbContext' in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext;
                } else if (_.isEmpty(self.dbContext)) {
                    self.dbContext = new PAdbContext();
                }

                if (_.isEmpty(this.dbContext)) {
                    const sError = 'Unable to determine dbContext for Customer Type.';
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                if ('RecordResolver' in self.LoadOptions) {
                    recordResolver = (<any>self.LoadOptions).RecordResolver;
                    recordResolverLoadPromise = ASync.resolve();
                } else {
                    recordResolver = CustomerTypeBO.createRecordResolver({dbContext: self.dbContext});
                    recordResolverLoadPromise = CustomerTypeBO.setupRecordResolver(recordResolver, [anId]);
                }

                await recordResolverLoadPromise

                const aCustomerTypeRow: any = recordResolver.get(esWB_Customer_Type, anId);

                if (_.isEmpty(aCustomerTypeRow)) {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception('Id not found.');
                    return self;
                }

                self.drWB_CustomerType = aCustomerTypeRow;
                self.updateIdentity();
                await self.IEntity_OnLoadComplete()

                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                self.LoadProgressProvider.finish();
                return self
            } catch (err: any) {
                self._OnLoadComplete = undefined;
                self.LoadProgressProvider.exception(err);
                return self;
            }
        })();

        return olc;
    }

    private updateIdentity(): void {
        const {
            WB_CUSTM_ID,
            CUSTM_ID,
            CUSTM_NAME
        } = this.WB_CustomerType.cif;
        this.Identity.setAll(WB_CUSTM_ID, CUSTM_ID, CUSTM_NAME);
        this.LoadProgressProvider.setName(
            'Loading ' + this.Identity.name + '(' + this.Identity.id_ui + ')'
        );
        this.Identity.listenToRecordUpdates(this.WB_CustomerType, 'WB_CUSTM_ID', 'CUSTM_ID', 'CUSTM_NAME');
        let userContext = PAEnvironment.getValue(PAEnvironment.UserContext, 'UserContext')
        let bHasRole = userContext.hasRoleId("Customer_Data_Editor")

        if (bHasRole)
            this.Identity.enableRecordWriteBack(this.WB_CustomerType, 'CUSTM_ID', 'CUSTM_NAME')
        else
            this.Identity.enableRecordWriteBack(this.WB_CustomerType, undefined, undefined)

    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            'Validating ' + self.WB_CustomerType.cif.CUSTM_NAME + '(' + self.WB_CustomerType.cif.CUSTM_ID + ')'
        )

        self.ValidateOption = options || {};

        try {
            if (self.dbContext == undefined) {
                const sError = 'Unable to determine dbContext to validate Customer Type.';
                self.ValidateProgressProvider.exception(sError);
                throw sError;
            }

            await CustomerTypeBOValidations.ValidateFields(self)
            if (self.EntityMetaData) {
                self.EntityMetaData.WB_EntityRow.cif.ENTITY_ID = self.WB_CustomerType.cif.CUSTM_ID
                self.EntityMetaData.WB_EntityRow.cif.ENTITY_NAME = self.WB_CustomerType.cif.CUSTM_NAME
                await self.EntityMetaData.Validate()
                self.ValidateMessageProvider.addMessages(self.EntityMetaData.ValidateMessageProvider.Messages)
            }

            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                self._bIsValidated = false;
            } else {
                self._bIsValidated = true;
            }
            self.ValidateProgressProvider.finish();

        } catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);

        }
        return self
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: { bSkipValidations?: boolean }): Promise<IBusinessObject> {
        const self = this;
        const wbCustomerType = <IwbCustomerType>self.drWB_CustomerType;
        this.SaveProgressProvider.start(
            'Saving ' + wbCustomerType.CUSTM_NAME + '(' + wbCustomerType.CUSTM_ID + ')'
        );

        try {
            if (_.isEmpty(self.dbContext)) {
                self._bIsSaved = false;
                const sError = 'Unable to determine dbContext to Save Customer Type.';
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;

            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            if (!options?.bSkipValidations) {
                await self.Validate({})

                if (!self.bIsValidated) {
                    self._bIsSaved = false;
                    self.SaveProgressProvider.finish();
                    return self
                }
            }

            let roles = await self.dbContext.findAll(esWB_Customer_Role, {
                where: {
                    DELETED_IND: {neq: '1'},
                    ROLE_ID: self.drWB_CustomerType.cif.CUSTM_ID,
                    WB_MDSET_ID: self.drWB_CustomerType.cif.WB_MDSET_ID
                }
            })
            if (_.isEmpty(roles)) {
                let roleRow: WB_CustomerRoleRow = <WB_CustomerRoleRow>await self.dbContext.createRecordWithKey(esWB_Customer_Role)
                roleRow.setDefaults()
                roleRow.cif.ROLE_ID = self.drWB_CustomerType.cif.CUSTM_ID
                roleRow.cif.ROLE_NAME = self.drWB_CustomerType.cif.CUSTM_NAME
                roleRow.cif.WB_CUSTM_ID = self.drWB_CustomerType.cif.WB_CUSTM_ID
                roleRow.cif.ENTERPRISE_SET_ID = self.drWB_CustomerType.cif.ENTERPRISE_SET_ID
                roleRow.cif.WB_MDSET_ID = self.drWB_CustomerType.cif.WB_MDSET_ID
                await saveFunction(esWB_Customer_Role, roleRow)
            }
            await saveFunction(esWB_Customer_Type, self.drWB_CustomerType, {})


            if (self.EntityMetaData) {
                await self.EntityMetaData.Save(bBatch)
            }
            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                });
            } else {
                self._bIsSaved = true;
            }

            self.SaveProgressProvider.finish();
        } catch (err) {
            self.SaveProgressProvider.exception(err);

        } finally {
            return self
        }

    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private async DeleteUsing(bSaveNow: boolean, options?: { bSkipValidations?: boolean }): Promise<IBusinessObject> {
        const self = this;
        const wbCustomerType = <IwbCustomerType>this.drWB_CustomerType;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start(
            'Deleting ' + wbCustomerType.CUSTM_NAME + '(' + wbCustomerType.CUSTM_ID + ')'
        );

        try {
            self.drWB_CustomerType.markDeleted();
            if (self.EntityMetaData)
                self.EntityMetaData.Delete(bSaveNow)

            if (bSaveNow)
                await self.SaveUsing(false, {bSkipValidations: true})

            self._bIsDeleted = true;
            self.DeleteProgressProvider.finish();
        } catch (err) {
            self.DeleteProgressProvider.exception(err);
        }
        return self
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    async Copy(options?: any): Promise<IBusinessObject> {
        const self = this

        const src: CustomerTypeBO = this;

        const tgt = new CustomerTypeBO({});
        await tgt.LoadNew()

        if (!tgt.bIsLoaded)
            throw new Error("Unable to create New Customer Type")

        RecordUtils.CopyProperties(tgt.dbContext, esWB_Customer_Type,
            src.WB_CustomerType, tgt.WB_CustomerType,
            [],
            ['DELETED_DT', 'DELETED_IND'],
            {})

        tgt.WB_CustomerType.cif.CUSTM_ID = "CopyOf" + src.WB_CustomerType.cif.CUSTM_ID
        tgt.WB_CustomerType.cif.CUSTM_NAME = "Copy of " + src.WB_CustomerType.cif.CUSTM_NAME
        tgt.Identity.setAll(tgt.WB_CustomerType.cif.WB_CUSTM_ID, tgt.WB_CustomerType.cif.CUSTM_ID, tgt.WB_CustomerType.cif.CUSTM_NAME);

        return tgt
    }

    //</editor-fold>

    //<editor-fold desc = "Entity">
    _entityMetaData: IEntityMetaData

    IEntity_init(): void {
        // Nothing can be created here
    }

    async IEntity_OnLoadComplete(): Promise<void> {
        const self = this

        let eMgr = new EntityMetaDataManager()
        let metaWbId = this.WB_CustomerType.cif.WB_CUSTM_ID
        let entityId = this.WB_CustomerType.cif.CUSTM_ID
        let entityName = this.WB_CustomerType.cif.CUSTM_NAME
        let wbMasterDataSetId = this.WB_CustomerType.cif.WB_MDSET_ID


        let emd = await eMgr.findOrCreate({
            dbContext: this.dbContext,
            appExecId: [entityId, "Customer"],
            dataObjectId: "/AZR/WB_CUST",
            dataObjectTypeId: "Table",
            setType: "Customer",
            metaType: "WB_CustM",
            metaWbId: metaWbId,
            wbMasterDataSetId: wbMasterDataSetId,
            entityId: entityId,
            entityName: entityName,
            dimensionId: "Customer"
        })

        this._entityMetaData = emd

    }

    get EntityMetaData(): IEntityMetaData {
        return this._entityMetaData
    }


    //</editor-fold>

    //<editor-fold desc = "staticIRecordResolver">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;
        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const customer_type_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {
                [esWB_Entity]: [esWB_DataObject, esWB_UI_AppExecutable, esWB_MasterData_Set]
            }
        };

        return new ESRecordResolver(dbContext, customer_type_options);
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: (string | number)[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_Customer_Type, ids)
                .then(() => {
                    resolve();
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    //</editor-fold>

}

PAEnvironment.registerBusinessObjectClass(CustomerTypeBO, "CustomerTypeBO", "Customer Type")